import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from './cn';

/**
 * Dropdown / Menu — Design System v2 (docs/design-system-v2.html §5.dropdown).
 *
 * Uso:
 *   <Dropdown trigger={<Button>Ações ▾</Button>}>
 *     <MenuItem icon={<IconEdit/>} onClick={…}>Editar</MenuItem>
 *     <MenuSeparator />
 *     <MenuItem variant="danger" onClick={…}>Excluir</MenuItem>
 *   </Dropdown>
 *
 * Fecha ao clicar fora (mousedown global) e ao apertar Esc. O trigger
 * recebe foco quando o menu fecha — boa prática de acessibilidade.
 */

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  /** Alinhamento do menu em relação ao trigger. */
  align?: 'left' | 'right';
}

export function Dropdown({ trigger, children, align = 'right' }: DropdownProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOut = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOut);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOut);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <div
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex"
      >
        {trigger}
      </div>
      {open && (
        <div
          role="menu"
          className={cn(
            'absolute top-[calc(100%+6px)] min-w-[200px] z-[60] bg-bg-elevated border border-border-v2 rounded-md shadow-lg p-1.5',
            'animate-[tcl-toast-in_0.16s_var(--ease)]',
            align === 'right' ? 'right-0' : 'left-0',
          )}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

interface MenuItemProps {
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

export function MenuItem({
  icon,
  children,
  onClick,
  variant = 'default',
  disabled,
}: MenuItemProps): JSX.Element {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-[14px] cursor-pointer transition-colors',
        variant === 'danger'
          ? 'text-danger-v2 hover:bg-danger-v2-bg'
          : 'text-text-secondary hover:bg-bg-muted hover:text-text-primary',
        disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent hover:text-text-secondary',
      )}
    >
      {icon && <span className="shrink-0 inline-flex">{icon}</span>}
      {children}
    </button>
  );
}

export function MenuSeparator(): JSX.Element {
  return <div role="separator" aria-hidden="true" className="h-px bg-border-v2 my-1.5" />;
}
